
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
    copyTextToClipboard,
    formatAuthorsForDisplay,
    formatCitation,
    getCitationActor
} from '../utils/thesisCitation';

const Home = ({ user }) => {
    const [groupedData, setGroupedData] = useState({});
    const [expandedDepts, setExpandedDepts] = useState({});
    const [expandedDegrees, setExpandedDegrees] = useState({});
    const [expandedMajors, setExpandedMajors] = useState({});
    const [lastUpdated, setLastUpdated] = useState('');
    const [allTheses, setAllTheses] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [selectedThesis, setSelectedThesis] = useState(null);
    const [viewPdf, setViewPdf] = useState(false);
    const [selectedFileUrl, setSelectedFileUrl] = useState('');
    const [copiedCitationId, setCopiedCitationId] = useState(null);
    const [showDetailRequestForm, setShowDetailRequestForm] = useState(false);
    const [requestType, setRequestType] = useState('admin');

    const [showRequestForm, setShowRequestForm] = useState(false);
    const [requestDeptFilter, setRequestDeptFilter] = useState('');
    const [requestDegreeId, setRequestDegreeId] = useState('');
    const [requestMajorId, setRequestMajorId] = useState('');
    const [requestThesisId, setRequestThesisId] = useState('');
    const [requestSearch, setRequestSearch] = useState('');
    const [requestPickerOpen, setRequestPickerOpen] = useState(false);
    const [requestData, setRequestData] = useState({ name: '', email: '', message: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const truncateOption = (text, max = 70) => {
        if (!text) return '';
        const str = String(text);
        return str.length > max ? `${str.slice(0, max - 1)}...` : str;
    };

    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim());

    const getSignedUrl = async (filePath, { silent = false } = {}) => {
        if (!filePath) return '';
        if (/^https?:\/\//i.test(filePath)) return filePath;
        if (!user?.user_id) return '';
        try {
            const res = await axios.get('/api/files/signed', {
                params: { path: filePath, userId: user.user_id }
            });
            return res.data?.url || '';
        } catch (err) {
            if (!silent) {
                alert(err.response?.data?.error || 'Unable to access this file.');
            }
            return '';
        }
    };

    const getFilenameFromPath = (filePath) => {
        if (!filePath) return 'thesis.pdf';
        const withoutQuery = filePath.split('?')[0];
        const name = withoutQuery.split('/').pop() || 'thesis.pdf';
        return name.toLowerCase().endsWith('.pdf') ? name : `${name}.pdf`;
    };

    const getDegreeName = (degreeId) => {
        if (!degreeId) return '';
        for (const dept of departments) {
            const found = (dept.degrees || []).find(d => String(d.degree_id) === String(degreeId));
            if (found) return found.degree_name;
        }
        return '';
    };

    const getMajorName = (majorId) => {
        if (!majorId) return '';
        for (const dept of departments) {
            for (const degree of dept.degrees || []) {
                const found = (degree.majors || []).find(m => String(m.major_id) === String(majorId));
                if (found) return found.major_name;
            }
        }
        return '';
    };

    const getCitationText = (thesis) => formatCitation(thesis, {
        getDegreeName,
        getMajorName
    });

    const canViewFullPdfForThesis = (thesis) => {
        if (!thesis?.file_path) return false;
        return Boolean(thesis && user);
    };

    const fetchHomeData = async () => {
        try {
            const res = await axios.get('/api/theses/search', {
                params: {
                    keyword: '',
                    year: '',
                    department: '',
                    sortBy: 'latest_desc',
                    ...getCitationActor(user)
                }
            });
            const deptRes = await axios.get('/api/departments');
            setAllTheses(res.data || []);
            setDepartments(deptRes.data || []);

            const deptInfoMap = {};
            deptRes.data.forEach(dept => {
                let degrees = Array.isArray(dept.degrees) ? dept.degrees : [];
                if ((!degrees || degrees.length === 0) && Array.isArray(dept.majors) && dept.majors.length > 0) {
                    degrees = [
                        {
                            degree_id: null,
                            degree_name: 'General',
                            majors: dept.majors
                        }
                    ];
                }
                const degreeById = {};
                degrees.forEach(d => {
                    degreeById[d.degree_id] = d;
                });
                deptInfoMap[dept.dept_name] = {
                    degrees,
                    degreeById,
                    description: dept.description || ''
                };
            });

            const grouped = {};
            res.data.forEach(thesis => {
                const dept = thesis.department || 'Unknown Department';
                const year = thesis.year || 'Unknown Year';
                const deptInfo = deptInfoMap[dept] || { degrees: [], degreeById: {}, description: '' };
                const hasDegrees = (deptInfo.degrees || []).length > 0;

                if (!grouped[dept]) {
                    grouped[dept] = { hasDegrees, data: {}, description: deptInfo.description || '' };
                }

                if (hasDegrees) {
                    const degreeId = thesis.degree_id || null;
                    const degreeObj = degreeId ? deptInfo.degreeById[degreeId] : null;
                    const degreeName = degreeObj
                        ? degreeObj.degree_name
                        : (degreeId ? (thesis.degree_name || 'Unknown Degree') : 'Unassigned Degree');
                    const degreeMajors = degreeObj?.majors || [];
                    const hasMajors = degreeMajors.length > 0;

                    if (!grouped[dept].data[degreeName]) {
                        grouped[dept].data[degreeName] = {
                            degreeId: degreeId || null,
                            hasMajors,
                            data: {}
                        };
                    }

                    const degreeBucket = grouped[dept].data[degreeName];

                    if (degreeBucket.hasMajors) {
                        let majorName = null;
                        if (thesis.major_id) {
                            const foundMajor = degreeMajors.find(m => m.major_id === thesis.major_id);
                            majorName = foundMajor?.major_name || null;
                        }
                        if (!majorName) {
                            majorName = thesis.major_name || 'General';
                        }
                        if (!degreeBucket.data[majorName]) {
                            degreeBucket.data[majorName] = { majorId: thesis.major_id || null, years: {} };
                        }
                        if (!degreeBucket.data[majorName].years[year]) {
                            degreeBucket.data[majorName].years[year] = [];
                        }
                        degreeBucket.data[majorName].years[year].push(thesis);
                    } else {
                        if (!degreeBucket.data[year]) {
                            degreeBucket.data[year] = [];
                        }
                        degreeBucket.data[year].push(thesis);
                    }
                } else {
                    if (!grouped[dept].data[year]) {
                        grouped[dept].data[year] = [];
                    }
                    grouped[dept].data[year].push(thesis);
                }
            });

            setGroupedData(grouped);

            const lastUpRes = await axios.get('/api/logs?limit=1');
            if (lastUpRes.data[0]) {
                setLastUpdated(new Date(lastUpRes.data[0].created_at).toLocaleString());
            }
        } catch (error) {
            console.error('Home fetch error', error);
        }
    };

    useEffect(() => {
        fetchHomeData();
    }, []);

    useEffect(() => {
        if (!selectedThesis?.thesis_id) return;
        const updated = allTheses.find(t => String(t.thesis_id) === String(selectedThesis.thesis_id));
        if (updated) {
            setSelectedThesis(updated);
        }
    }, [allTheses, selectedThesis?.thesis_id]);

    const toggleDepartment = (dept) => {
        setExpandedDepts(prev => ({
            ...prev,
            [dept]: !prev[dept]
        }));
    };

    const toggleDegree = (deptDegreeKey) => {
        setExpandedDegrees(prev => ({
            ...prev,
            [deptDegreeKey]: !prev[deptDegreeKey]
        }));
    };

    const toggleMajor = (deptMajorKey) => {
        setExpandedMajors(prev => ({
            ...prev,
            [deptMajorKey]: !prev[deptMajorKey]
        }));
    };

    const getTotalThesisCount = () => {
        let total = 0;
        Object.keys(groupedData).forEach(dept => {
            const deptData = groupedData[dept];
            if (deptData?.hasDegrees) {
                Object.keys(deptData.data).forEach(degree => {
                    const degreeData = deptData.data[degree];
                    if (degreeData?.hasMajors) {
                        Object.keys(degreeData.data).forEach(major => {
                            Object.keys(degreeData.data[major].years || {}).forEach(year => {
                                total += degreeData.data[major].years[year].length;
                            });
                        });
                    } else {
                        Object.keys(degreeData?.data || {}).forEach(year => {
                            total += degreeData.data[year].length;
                        });
                    }
                });
            } else {
                Object.keys(deptData?.data || {}).forEach(year => {
                    total += deptData.data[year].length;
                });
            }
        });
        return total;
    };

    const getDepartmentCount = (dept) => {
        let count = 0;
        const deptData = groupedData[dept];
        if (deptData?.hasDegrees) {
            Object.keys(deptData.data).forEach(degree => {
                const degreeData = deptData.data[degree];
                if (degreeData?.hasMajors) {
                    Object.keys(degreeData.data).forEach(major => {
                        Object.keys(degreeData.data[major].years || {}).forEach(year => {
                            count += degreeData.data[major].years[year].length;
                        });
                    });
                } else {
                    Object.keys(degreeData?.data || {}).forEach(year => {
                        count += degreeData.data[year].length;
                    });
                }
            });
        } else {
            Object.keys(deptData?.data || {}).forEach(year => {
                count += deptData.data[year].length;
            });
        }
        return count;
    };

    const getDegreeCount = (dept, degree) => {
        let count = 0;
        const deptData = groupedData[dept];
        const degreeData = deptData?.data?.[degree];
        if (!degreeData) return 0;
        if (degreeData.hasMajors) {
            Object.keys(degreeData.data || {}).forEach(major => {
                Object.keys(degreeData.data[major].years || {}).forEach(year => {
                    count += degreeData.data[major].years[year].length;
                });
            });
        } else {
            Object.keys(degreeData.data || {}).forEach(year => {
                count += degreeData.data[year].length;
            });
        }
        return count;
    };

    const getMajorCount = (dept, degree, major) => {
        let count = 0;
        const majorData = groupedData[dept]?.data?.[degree]?.data?.[major];
        Object.keys(majorData?.years || {}).forEach(year => {
            count += majorData.years[year].length;
        });
        return count;
    };

    const sortThesesByTitle = (theses = []) => (
        [...theses].sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')))
    );

    const getMajorTheses = (majorData) => sortThesesByTitle(
        Object.values(majorData?.years || {}).flat()
    );

    const getDegreeTheses = (degreeData) => sortThesesByTitle(
        Object.values(degreeData?.data || {}).flat()
    );

    const getDepartmentTheses = (deptData) => sortThesesByTitle(
        Object.values(deptData?.data || {}).flat()
    );

    useEffect(() => {
        setRequestDegreeId('');
        setRequestMajorId('');
    }, [requestDeptFilter]);

    useEffect(() => {
        setRequestMajorId('');
    }, [requestDegreeId]);

    useEffect(() => {
        let active = true;
        const loadUrl = async () => {
            if (!selectedThesis?.file_path) {
                setSelectedFileUrl('');
                return;
            }
            if (!canViewFullPdfForThesis(selectedThesis)) {
                setSelectedFileUrl('');
                return;
            }
            const url = await getSignedUrl(selectedThesis.file_path, { silent: true });
            if (active) setSelectedFileUrl(url);
        };
        loadUrl();
        return () => { active = false; };
    }, [selectedThesis?.file_path, user?.user_id, user?.role, user?.email]);

    const selectedThesisForRequest = allTheses.find(t => String(t.thesis_id) === String(requestThesisId));
    const canAuthorRequest = Boolean(selectedThesisForRequest && selectedThesisForRequest.author_data?.some(a => a.email));
    const selectedRequestDept = requestDeptFilter
        ? departments.find(d => d.dept_name === requestDeptFilter)
        : null;
    const degreesForRequestDept = selectedRequestDept?.degrees || [];
    const majorsForRequestDeptFallback = selectedRequestDept?.majors || [];
    const selectedRequestDegree = requestDegreeId
        ? degreesForRequestDept.find(d => String(d.degree_id) === String(requestDegreeId))
        : null;
    const majorsForRequestDegree = selectedRequestDegree?.majors || [];

    const applyViewUpdate = (thesisId, viewCount) => {
        const normalizedCount = Number(viewCount || 0);
        setAllTheses(prev => prev.map(thesis => (
            String(thesis.thesis_id) === String(thesisId)
                ? { ...thesis, view_count: normalizedCount }
                : thesis
        )));
        setSelectedThesis(prev => (
            prev && String(prev.thesis_id) === String(thesisId)
                ? { ...prev, view_count: normalizedCount }
                : prev
        ));
    };

    const applyCitationUpdate = (thesisId, citationCount, hasCited = true) => {
        const normalizedCount = Number(citationCount || 0);
        setAllTheses(prev => prev.map(thesis => (
            String(thesis.thesis_id) === String(thesisId)
                ? { ...thesis, citation_count: normalizedCount, has_cited: hasCited }
                : thesis
        )));
        setSelectedThesis(prev => (
            prev && String(prev.thesis_id) === String(thesisId)
                ? { ...prev, citation_count: normalizedCount, has_cited: hasCited }
                : prev
        ));
    };

    const handleCopyCitation = async (thesis) => {
        if (!thesis?.thesis_id) return;

        try {
            await copyTextToClipboard(getCitationText(thesis));
            setCopiedCitationId(thesis.thesis_id);

            if (!thesis.has_cited) {
                const res = await axios.post(`/api/theses/${thesis.thesis_id}/cite`, getCitationActor(user));
                applyCitationUpdate(thesis.thesis_id, res.data?.citation_count, true);
            }

            window.setTimeout(() => {
                setCopiedCitationId((currentId) => (
                    String(currentId) === String(thesis.thesis_id) ? null : currentId
                ));
            }, 2000);
        } catch (err) {
            if (err.response?.status === 409) {
                applyCitationUpdate(thesis.thesis_id, err.response.data?.citation_count, true);
                setCopiedCitationId(thesis.thesis_id);
                return;
            }

            console.error('Failed to copy citation:', err);
            alert('Failed to copy citation. Please try again.');
        }
    };

    const openThesisDetails = async (thesis) => {
        if (!thesis?.thesis_id) return;
        const latestThesis = allTheses.find((item) => String(item.thesis_id) === String(thesis.thesis_id)) || thesis;
        setSelectedThesis(latestThesis);
        setViewPdf(false);
        try {
            const res = await axios.post(`/api/theses/${thesis.thesis_id}/view`);
            applyViewUpdate(thesis.thesis_id, res.data?.view_count);
        } catch (err) {
            console.error('Failed to record thesis view:', err);
        }
    };

    const handleDownload = async (filePath) => {
        if (!filePath) {
            alert('PDF file unavailable.');
            return;
        }
        const url = await getSignedUrl(filePath);
        if (!url) return;
        try {
            const response = await axios.get(url, { responseType: 'blob' });
            const blobUrl = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = getFilenameFromPath(filePath);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            alert('Failed to download file. Please try again.');
        }
    };

    const submitRequest = async () => {
        if (!requestData.name || !requestData.email) {
            alert('Please provide your name and email.');
            return;
        }
        if (!isValidEmail(requestData.email)) {
            alert('Please enter a valid email address.');
            return;
        }

        setIsSubmitting(true);
        const selectedThesis = allTheses.find(t => String(t.thesis_id) === String(requestThesisId));
        const isSpecific = Boolean(selectedThesis);
        const payload = {
            ...requestData,
            thesis_id: isSpecific ? selectedThesis.thesis_id : null,
            thesis_title: isSpecific ? selectedThesis.title : 'General Request',
            type: isSpecific ? 'specific' : 'general'
        };

        try {
            await axios.post('/api/requests', payload);
            alert('Request sent successfully! Our staff will contact you via email.');
            setShowRequestForm(false);
            setRequestDeptFilter('');
            setRequestDegreeId('');
            setRequestMajorId('');
            setRequestThesisId('');
            setRequestSearch('');
            setRequestPickerOpen(false);
            setRequestData({ name: '', email: '', message: '' });
        } catch (err) {
            alert('Failed to send request. Please check your connection.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const submitDetailRequest = async () => {
        if (!requestData.name || !requestData.email) {
            alert('Please provide your name and email.');
            return;
        }
        if (!isValidEmail(requestData.email)) {
            alert('Please enter a valid email address.');
            return;
        }
        if (!selectedThesis) {
            alert('No thesis selected.');
            return;
        }

        setIsSubmitting(true);
        const payload = {
            ...requestData,
            thesis_id: selectedThesis.thesis_id,
            thesis_title: selectedThesis.title,
            type: 'specific'
        };

        try {
            await axios.post('/api/requests', payload);
            alert('Request sent successfully! Our staff will contact you via email.');
            setShowDetailRequestForm(false);
            setRequestType('admin');
            setRequestData({ name: '', email: '', message: '' });
        } catch (err) {
            alert('Failed to send request. Please check your connection.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const submitDetailAuthorRequest = async () => {
        if (!requestData.name || !requestData.email) {
            alert('Please provide your name and email.');
            return;
        }
        if (!isValidEmail(requestData.email)) {
            alert('Please enter a valid email address.');
            return;
        }
        if (!selectedThesis) {
            alert('Please select a specific thesis to request from authors.');
            return;
        }

        const authorsWithEmails = selectedThesis.author_data?.filter(a => a.email) || [];
        if (authorsWithEmails.length === 0) {
            alert('No authors have provided email addresses for this thesis.');
            return;
        }

        setIsSubmitting(true);
        const payload = {
            thesis_id: selectedThesis.thesis_id,
            author_ids: authorsWithEmails.map(a => a.author_id),
            requester_name: requestData.name,
            requester_email: requestData.email,
            message: requestData.message
        };

        try {
            await axios.post('/api/author-requests', payload);
            alert('Request sent to authors! They will review and respond via email.');
            setShowDetailRequestForm(false);
            setRequestType('admin');
            setRequestData({ name: '', email: '', message: '' });
        } catch (err) {
            const serverError = err.response?.data?.error;
            const serverDetails = err.response?.data?.details;
            alert(serverDetails ? `${serverError}: ${serverDetails}` : (serverError || 'Failed to send request. Please check your connection.'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const submitAuthorRequest = async () => {
        if (!requestData.name || !requestData.email) {
            alert('Please provide your name and email.');
            return;
        }
        if (!isValidEmail(requestData.email)) {
            alert('Please enter a valid email address.');
            return;
        }

        const selectedThesis = allTheses.find(t => String(t.thesis_id) === String(requestThesisId));
        if (!selectedThesis) {
            alert('Please select a specific thesis to request from authors.');
            return;
        }

        const authorsWithEmails = selectedThesis.author_data?.filter(a => a.email) || [];
        if (authorsWithEmails.length === 0) {
            alert('No authors have provided email addresses for this thesis.');
            return;
        }

        setIsSubmitting(true);
        const payload = {
            thesis_id: selectedThesis.thesis_id,
            author_ids: authorsWithEmails.map(a => a.author_id),
            requester_name: requestData.name,
            requester_email: requestData.email,
            message: requestData.message
        };

        try {
            await axios.post('/api/author-requests', payload);
            alert('Request sent to authors! They will review and respond via email.');
            setShowRequestForm(false);
            setRequestDeptFilter('');
            setRequestDegreeId('');
            setRequestMajorId('');
            setRequestThesisId('');
            setRequestSearch('');
            setRequestPickerOpen(false);
            setRequestData({ name: '', email: '', message: '' });
        } catch (err) {
            const serverError = err.response?.data?.error;
            const serverDetails = err.response?.data?.details;
            alert(serverDetails ? `${serverError}: ${serverDetails}` : (serverError || 'Failed to send request. Please check your connection.'));
        } finally {
            setIsSubmitting(false);
        }
    };
    return (
        <div className="page">
            <style>
                {`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                @media (max-width: 640px) and (orientation: portrait) {
                    .home-feature-divider {
                        width: 100% !important;
                        height: 1px !important;
                        align-self: auto !important;
                        background: linear-gradient(90deg, rgba(128, 0, 0, 0) 0%, rgba(128, 0, 0, 0.18) 18%, rgba(128, 0, 0, 0.18) 82%, rgba(128, 0, 0, 0) 100%) !important;
                    }
                }
                `}
            </style>
            <div className="home-hero">
                <h1>NVC Thesis Repository</h1>
                <p className="subtitle" style={{ maxWidth: '700px', margin: '0 auto' }}>
                    A centralized repository of academic excellence. Browse through years of research,
                    innovative projects, and scholarly work from our various departments.
                </p>
            </div>

            <div style={homeFeatureStripStyle}>
                <div style={homeFeatureColumnStyle}>
                    <span style={homeFeatureKickerStyle}>Archive Feature</span>
                    <h3 style={homeFeatureTitleStyle}>Explore</h3>
                    <p style={homeFeatureTextStyle}>Access abstracts and metadata for hundreds of archived theses.</p>
                </div>
                <div className="home-feature-divider" style={homeFeatureColumnDividerStyle} aria-hidden="true" />
                <div style={homeFeatureColumnStyle}>
                    <span style={homeFeatureKickerStyle}>Access Support</span>
                    <h3 style={homeFeatureTitleStyle}>Request a Thesis</h3>
                    <p style={homeFeatureTextStyle}>Full PDF access can be granted via email request.</p>
                </div>
                <div className="home-feature-divider" style={homeFeatureColumnDividerStyle} aria-hidden="true" />
                <div style={homeFeatureColumnStyle}>
                    <span style={homeFeatureKickerStyle}>Digital Mission</span>
                    <h3 style={homeFeatureTitleStyle}>Preserve</h3>
                    <p style={homeFeatureTextStyle}>Digitally archiving our intellectual heritage for future generations.</p>
                </div>
            </div>

            <div style={{ padding: '20px', maxWidth: '1000px', margin: '30px auto 0 auto', fontFamily: 'inherit' }}>
                <div style={{ marginBottom: '20px' }}>
                    <h2 style={{ color: '#333', marginBottom: '5px' }}>Departments</h2>
                    {lastUpdated && <p style={{ color: '#666', fontSize: '0.8rem' }}>Last Collection Update: {lastUpdated}</p>}
                </div>

                <div style={journalContainerStyle}>
                    <div style={journalHeaderStyle} className="on-dark">
                        <h3 style={{ margin: 0 }}>Departments <span style={{ color: '#fff', fontWeight: 'bold' }}>[{getTotalThesisCount()}]</span></h3>
                    </div>

                    {Object.keys(groupedData).length === 0 ? (
                        <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>No theses available</div>
                    ) : (
                        Object.keys(groupedData).sort().map((dept) => {
                            const deptData = groupedData[dept];
                            const hasDegrees = deptData.hasDegrees;
                            return (
                                <div key={dept} style={departmentContainerStyle}>
                                    <div style={departmentHeaderStyle}>
                                        <button
                                            onClick={() => toggleDepartment(dept)}
                                            style={expandButtonStyle}
                                        >
                                            {expandedDepts[dept] ? '-' : '+'}
                                        </button>
                                        <div style={{ flex: 1 }}>
                                            <h4 style={departmentNameStyle}>{dept}</h4>
                                            {groupedData[dept]?.description && <p style={{ margin: '3px 0 0 0', fontSize: '0.85rem', color: '#666', fontStyle: 'italic' }}>{groupedData[dept].description}</p>}
                                        </div>
                                        <span style={countBadgeStyle}>{getDepartmentCount(dept)}</span>
                                    </div>

                                    {expandedDepts[dept] && (
                                        <div style={yearsListStyle}>
                                            {hasDegrees ? (
                                                Object.keys(deptData.data).sort().map((degree) => {
                                                    const degreeKey = `${dept}::${degree}`;
                                                    const degreeData = deptData.data[degree];
                                                    return (
                                                        <div key={degreeKey} style={{ marginBottom: '15px', borderLeft: '2px solid #ccc', paddingLeft: '12px' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', padding: '5px 0' }}>
                                                                <button
                                                                    onClick={() => toggleDegree(degreeKey)}
                                                                    style={{ ...expandButtonStyle, width: '24px', height: '24px', fontSize: '14px' }}
                                                                >
                                                                    {expandedDegrees[degreeKey] ? '-' : '+'}
                                                                </button>
                                                                <div style={{ fontWeight: '600', color: '#555', fontSize: '0.95rem', flex: 1 }}>
                                                                    {degree} <span style={{ fontSize: '0.85rem', color: '#888' }}>({getDegreeCount(dept, degree)})</span>
                                                                </div>
                                                            </div>
                                                            {expandedDegrees[degreeKey] && (
                                                                <div style={{ paddingLeft: '8px' }}>
                                                                    {degreeData.hasMajors ? (
                                                                        Object.keys(degreeData.data).sort().map((major) => {
                                                                            const majorKey = `${dept}::${degree}::${major}`;
                                                                            const majorData = degreeData.data[major];
                                                                            return (
                                                                                <div key={majorKey} style={{ marginBottom: '12px', borderLeft: '2px solid #ddd', paddingLeft: '12px' }}>
                                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', padding: '5px 0' }}>
                                                                                        <button
                                                                                            onClick={() => toggleMajor(majorKey)}
                                                                                            style={{ ...expandButtonStyle, width: '24px', height: '24px', fontSize: '14px' }}
                                                                                        >
                                                                                            {expandedMajors[majorKey] ? '-' : '+'}
                                                                                        </button>
                                                                                        <div style={{ fontWeight: '600', color: '#555', fontSize: '0.95rem', flex: 1 }}>
                                                                                            {major} <span style={{ fontSize: '0.85rem', color: '#888' }}>({getMajorCount(dept, degree, major)})</span>
                                                                                        </div>
                                                                                    </div>
                                                                                    {expandedMajors[majorKey] && (
                                                                                        <div style={{ paddingLeft: '8px' }}>
                                                                                            {getMajorTheses(majorData).map((thesis) => {
                                                                                                return (
                                                                                                    <div key={`${majorKey}-${thesis.thesis_id}`} style={yearRowStyle}>
                                                                                                        <div style={thesisRowStyle}>
                                                                                                            <span style={yearNameStyle}>{thesis.title} - {thesis.year || 'N/A'}</span>
                                                                                                            <button onClick={() => openThesisDetails(thesis)} style={thesisViewBtnStyle}>
                                                                                                                View
                                                                                                            </button>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                );
                                                                                            })}
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            );
                                                                        })
                                                                    ) : (
                                                                        getDegreeTheses(degreeData).map((thesis) => {
                                                                            return (
                                                                                <div key={`${degreeKey}-${thesis.thesis_id}`} style={yearRowStyle}>
                                                                                    <div style={thesisRowStyle}>
                                                                                        <span style={yearNameStyle}>{thesis.title} - {thesis.year || 'N/A'}</span>
                                                                                        <button onClick={() => openThesisDetails(thesis)} style={thesisViewBtnStyle}>
                                                                                            View
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                getDepartmentTheses(deptData).map((thesis) => (
                                                    <div key={`${dept}-${thesis.thesis_id}`} style={yearRowStyle}>
                                                        <div style={thesisRowStyle}>
                                                            <span style={yearNameStyle}>{thesis.title} - {thesis.year || 'N/A'}</span>
                                                            <button onClick={() => openThesisDetails(thesis)} style={thesisViewBtnStyle}>
                                                                View
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>

                <div style={{ ...bannerStyle, marginTop: '30px' }}>
                    <div>
                        <h4 style={{ margin: 0, color: '#004085' }}>Want a specific thesis?</h4>
                        <p style={{ margin: 0, color: '#004085', fontSize: '0.9rem' }}>Submit a request and our staff will assist you.</p>
                    </div>
                    <button
                        onClick={() => {
                            setRequestDeptFilter('');
                            setRequestDegreeId('');
                            setRequestMajorId('');
                            setRequestThesisId('');
                            setRequestSearch('');
                            setRequestPickerOpen(false);
                            setRequestData({ name: '', email: '', message: '' });
                            setShowRequestForm(true);
                        }}
                        style={{ ...btnStyle, background: '#007bff', fontWeight: 'bold' }}
                    >
                        Request PDF File
                    </button>
                </div>
            </div>

            {selectedThesis && (
                (() => {
                    const hasPdf = Boolean(selectedThesis?.file_path);
                    const canViewFullPdf = hasPdf && Boolean(user);
                    const canDownload = hasPdf && Boolean(user) && ['admin', 'staff'].includes(user.role);
                    const canSeePdfUnavailable = !hasPdf && Boolean(user) && ['admin', 'staff'].includes(user.role);
                    const canRequestAuthors = Boolean(selectedThesis?.author_data?.some(a => a.email));

                    const handleTogglePdf = () => {
                        if (!canViewFullPdf) return;
                        setViewPdf(!viewPdf);
                    };

                    return (
                        <div style={modalOverlay} onClick={() => {
                            setSelectedThesis(null);
                            setViewPdf(false);
                        }}>
                            <div style={{ ...modalContent, width: viewPdf ? '95%' : '550px', height: viewPdf ? '90vh' : undefined }} onClick={(e) => e.stopPropagation()}>
                                <h3 style={{ margin: '0 0 5px 0', color: '#333' }}>{selectedThesis.title}</h3>

                                <p style={{ color: '#666', fontSize: '0.9rem', marginTop: 0, marginBottom: '15px' }}>
                                    Views: {Number(selectedThesis.view_count || 0)} | Citations: {Number(selectedThesis.citation_count || 0)}
                                </p>

                                {!viewPdf ? (
                                    <div style={{ flex: 1, overflowY: 'auto' }}>
                                        {canSeePdfUnavailable && (
                                            <div style={{ marginBottom: '12px', padding: '10px', borderRadius: '6px', background: '#fff3cd', border: '1px solid #ffeeba', color: '#856404', fontSize: '0.9rem' }}>
                                                PDF file unavailable for this thesis.
                                            </div>
                                        )}
                                        {(() => {
                                            const degreeName = selectedThesis.degree_id ? getDegreeName(selectedThesis.degree_id) : '';
                                            const majorName = selectedThesis.major_id ? getMajorName(selectedThesis.major_id) : '';
                                            const deptDisplay = [selectedThesis.department, degreeName, majorName].filter(Boolean).join(' - ');
                                            return <p><strong>{selectedThesis.year}</strong> &middot; {deptDisplay}</p>;
                                        })()}

                                        {selectedThesis.authors && selectedThesis.authors.length > 0 && (
                                            <p style={{ margin: '6px 0', color: '#333' }}>
                                                <strong>Authors:</strong> {formatAuthorsForDisplay(selectedThesis.authors)}
                                            </p>
                                        )}

                                        {selectedThesis.keywords && selectedThesis.keywords.length > 0 && (
                                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                                                {selectedThesis.keywords.map((k, i) => (
                                                    <span key={i} style={{ background: '#eef', padding: '6px 8px', borderRadius: '12px', fontSize: '13px' }}>
                                                        {k}
                                                    </span>
                                                ))}
                                            </div>
                                        )}

                                        <h5 style={{ marginTop: '15px', marginBottom: '8px', color: '#333' }}>Abstract</h5>
                                        <div style={abstractBox}>{selectedThesis.abstract}</div>

                                        <div style={citationPanelStyle}>
                                            <div style={citationHeaderRow}>
                                                <h5 style={{ margin: 0, color: '#333' }}>Citation</h5>
                                            </div>
                                            <p style={citationStyle}>{getCitationText(selectedThesis)}</p>
                                            <div style={citationFooterRow}>
                                                <span style={citationCountText}>
                                                    Citation count: {Number(selectedThesis.citation_count || 0)}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleCopyCitation(selectedThesis)}
                                                    style={copyCitationBtn}
                                                >
                                                    {String(copiedCitationId) === String(selectedThesis.thesis_id) ? 'Copied' : 'Copy Citation'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ) : canViewFullPdf ? (
                                    <div style={{ flex: 1, background: '#525659', borderRadius: '8px', overflow: 'hidden', height: '85vh' }}>
                                        <iframe title="PDF" src={`${selectedFileUrl}#toolbar=0`} width="100%" height="100%" style={{ border: 'none' }} />
                                    </div>
                                ) : (
                                    <div style={restrictedOverlay} className="restricted-overlay">
                                        <h2>Full PDF Access Restricted</h2>
                                        <p>Only logged-in staff and admins can view the complete document.</p>
                                        <p style={{ fontSize: '0.9rem', marginTop: '15px' }}>Request access to this thesis to receive the full PDF via email.</p>
                                    </div>
                                )}

                                <div style={{ display: 'flex', gap: '10px', marginTop: '20px', flexWrap: 'wrap' }}>
                                    {user ? (
                                        <button
                                            onClick={handleTogglePdf}
                                            style={{ ...readBtn, opacity: canViewFullPdf ? 1 : 0.5, cursor: canViewFullPdf ? 'pointer' : 'not-allowed' }}
                                            disabled={!canViewFullPdf}
                                            title={canViewFullPdf ? '' : (hasPdf ? 'Only staff and admins can view the full PDF.' : 'PDF file unavailable.')}
                                        >
                                            {viewPdf ? 'Show Abstract' : 'Read Full PDF'}
                                        </button>
                                    ) : (
                                        <>
                                            <button
                                                onClick={() => { setRequestType('author'); setShowDetailRequestForm(true); }}
                                                disabled={!canRequestAuthors}
                                                style={{
                                                    ...requestBtn,
                                                    background: canRequestAuthors ? '#007bff' : '#6c757d',
                                                    opacity: canRequestAuthors ? 1 : 0.6,
                                                    cursor: canRequestAuthors ? 'pointer' : 'not-allowed'
                                                }}
                                                title={canRequestAuthors ? '' : 'No author emails are available for this thesis.'}
                                            >
                                                Request PDF file from Author/s
                                            </button>
                                            {user?.role !== 'admin' && (
                                                <button onClick={() => { setRequestType('admin'); setShowDetailRequestForm(true); }} style={requestBtn}>
                                                    Request PDF file from Admin/s
                                                </button>
                                            )}
                                        </>
                                    )}

                                    {canDownload && (
                                        <button onClick={() => handleDownload(selectedThesis.file_path)} style={downloadBtn}>
                                            Download
                                        </button>
                                    )}

                                    <button onClick={() => {
                                        setSelectedThesis(null);
                                        setViewPdf(false);
                                    }} style={closeBtn}>
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })()
            )}

            {showRequestForm && (
                <div style={modalOverlay} onClick={() => setShowRequestForm(false)}>
                    <div style={{ ...modalContent, width: '550px' }} onClick={(e) => e.stopPropagation()}>
                        {isSubmitting && (
                            <div style={loaderInnerOverlay}>
                                <div style={spinnerStyle}></div>
                                <p style={{ fontWeight: 'bold', marginTop: '10px' }}>Submitting Request...</p>
                            </div>
                        )}

                        <div style={requestModalBody}>
                            <h3 style={{ marginTop: 0 }}>Request a Thesis</h3>
                            <p style={{ color: '#666', fontSize: '0.9rem' }}>
                                Request a thesis project's full PDF file by filling out the form below. You can optionally select a specific thesis to request from its authors. If no thesis is selected, your request will be treated as a general inquiry and our staff will assist you in finding relevant theses.
                            </p>

                            <div style={thesisPickerStyle}>
                                <button
                                    type="button"
                                    onClick={() => setRequestPickerOpen(!requestPickerOpen)}
                                    style={thesisPickerToggleStyle}
                                >
                                    <span style={{ fontWeight: 'bold' }}>Select a Thesis (Optional)</span>
                                    <span style={{ color: '#666', fontSize: '0.8rem' }}>
                                        {requestPickerOpen ? 'Hide filters' : 'Filter, search, then choose'}
                                    </span>
                                </button>
                                {requestPickerOpen && (
                                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '10px' }}>
                                        <select
                                            style={{ ...inputStyle, flex: '1 1 180px', maxWidth: '100%', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}
                                            value={requestDeptFilter}
                                            onChange={(e) => setRequestDeptFilter(e.target.value)}
                                        >
                                            <option value="">All Departments</option>
                                            {departments.map(d => (
                                                <option key={d.dept_id} value={d.dept_name}>{truncateOption(d.dept_name, 60)}</option>
                                            ))}
                                        </select>
                                        {degreesForRequestDept.length > 0 && (
                                            <select
                                                style={{ ...inputStyle, flex: '1 1 200px', maxWidth: '100%', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}
                                                value={requestDegreeId}
                                                onChange={(e) => setRequestDegreeId(e.target.value)}
                                            >
                                                <option value="">All Degrees</option>
                                                {degreesForRequestDept.map(d => (
                                                    <option key={d.degree_id} value={d.degree_id}>{truncateOption(d.degree_name, 60)}</option>
                                                ))}
                                            </select>
                                        )}
                                        {(majorsForRequestDegree.length > 0 || majorsForRequestDeptFallback.length > 0) && (
                                            <select
                                                style={{ ...inputStyle, flex: '1 1 180px', maxWidth: '100%', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}
                                                value={requestMajorId}
                                                onChange={(e) => setRequestMajorId(e.target.value)}
                                            >
                                                <option value="">All Majors</option>
                                                {(requestDegreeId ? majorsForRequestDegree : majorsForRequestDeptFallback).map(m => (
                                                    <option key={m.major_id} value={m.major_id}>{truncateOption(m.major_name, 60)}</option>
                                                ))}
                                            </select>
                                        )}
                                        <input
                                            type="text"
                                            placeholder="Search title..."
                                            value={requestSearch}
                                            onChange={(e) => setRequestSearch(e.target.value)}
                                            style={{ ...inputStyle, flex: '1 1 220px' }}
                                        />
                                        <select
                                            style={{ ...inputStyle, flex: '2 1 260px', maxWidth: '100%', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}
                                            value={requestThesisId}
                                            onChange={(e) => setRequestThesisId(e.target.value)}
                                        >
                                            <option value="">No specific thesis (General Request)</option>
                                            {(allTheses || [])
                                                .filter(t => !requestDeptFilter || t.department === requestDeptFilter)
                                                .filter(t => !requestDegreeId || String(t.degree_id || '') === String(requestDegreeId))
                                                .filter(t => !requestMajorId || String(t.major_id || '') === String(requestMajorId))
                                                .filter(t => !requestSearch || String(t.title || '').toLowerCase().includes(requestSearch.toLowerCase()))
                                                .sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')))
                                                .map(t => (
                                                    <option key={t.thesis_id} value={t.thesis_id}>
                                                        {truncateOption(t.title, 70)} ({t.year || 'N/A'})
                                                    </option>
                                                ))}
                                        </select>
                                    </div>
                                )}
                            </div>

                            <input
                                placeholder="Full Name"
                                style={{ ...inputStyle, width: '100%', marginBottom: '10px' }}
                                value={requestData.name}
                                onChange={e => setRequestData({ ...requestData, name: e.target.value })}
                            />
                            <input
                                type="email"
                                placeholder="Email Address"
                                style={{ ...inputStyle, width: '100%', marginBottom: '10px' }}
                                value={requestData.email}
                                onChange={e => setRequestData({ ...requestData, email: e.target.value })}
                            />
                            <textarea
                                placeholder="Purpose of request (e.g., Undergraduate Research)..."
                                style={{ ...inputStyle, width: '100%', height: '100px', marginBottom: '10px', resize: 'none' }}
                                value={requestData.message}
                                onChange={e => setRequestData({ ...requestData, message: e.target.value })}
                            />

                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                {user?.role !== 'admin' && (
                                    <button
                                        onClick={submitRequest}
                                        disabled={isSubmitting}
                                        style={{ ...btnStyle, flex: 1, opacity: isSubmitting ? 0.7 : 1 }}
                                    >
                                        {isSubmitting ? 'Sending...' : 'Admin Request'}
                                    </button>
                                )}
                                <button
                                    onClick={submitAuthorRequest}
                                    disabled={isSubmitting || !requestThesisId || !canAuthorRequest}
                                    style={{
                                        ...btnStyle,
                                        flex: 1,
                                        background: (!requestThesisId || !canAuthorRequest) ? '#6c757d' : '#007bff',
                                        opacity: (isSubmitting || !requestThesisId || !canAuthorRequest) ? 0.6 : 1,
                                        cursor: (isSubmitting || !requestThesisId || !canAuthorRequest) ? 'not-allowed' : 'pointer'
                                    }}
                                    title={
                                        !requestThesisId
                                            ? 'Select a specific thesis to request from authors.'
                                            : (!canAuthorRequest ? 'This thesis has no author emails on file.' : '')
                                    }
                                >
                                    {isSubmitting ? 'Sending...' : 'Author Request'}
                                </button>
                                <button
                                    onClick={() => { setShowRequestForm(false); }}
                                    style={closeBtn}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showDetailRequestForm && selectedThesis && (
                <div style={modalOverlay} onClick={() => setShowDetailRequestForm(false)}>
                    <div style={{ ...modalContent, width: '550px' }} onClick={(e) => e.stopPropagation()}>
                        {isSubmitting && (
                            <div style={loaderInnerOverlay}>
                                <div style={spinnerStyle}></div>
                                <p style={{ fontWeight: 'bold', marginTop: '10px' }}>Submitting Request...</p>
                            </div>
                        )}

                        <div style={requestModalBody}>
                            <h3 style={{ marginTop: 0 }}>
                                {requestType === 'author'
                                    ? 'Request PDF from Authors'
                                    : 'Request PDF from Administration'}
                            </h3>
                            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '15px' }}>
                                Requesting: <strong>{selectedThesis.title}</strong>
                            </p>

                            {requestType === 'author' && (
                                <p style={{ background: '#f0f7ff', padding: '10px', borderRadius: '4px', fontSize: '0.85rem', color: '#004085', marginBottom: '15px', borderLeft: '4px solid #0066cc' }}>
                                    Your request will be sent directly to the thesis author(s). They will review your request and may approve it to send you the full PDF file.
                                </p>
                            )}

                            {requestType === 'admin' && user?.role !== 'admin' && (
                                <p style={{ background: '#fff3cd', padding: '10px', borderRadius: '4px', fontSize: '0.85rem', color: '#856404', marginBottom: '15px', borderLeft: '4px solid #ffc107' }}>
                                    Your request will be submitted to our administration team. They will review and respond to you via email.
                                </p>
                            )}

                            <input
                                placeholder="Full Name"
                                style={{ ...inputStyle, width: '100%', marginBottom: '10px' }}
                                value={requestData.name}
                                onChange={e => setRequestData({ ...requestData, name: e.target.value })}
                            />
                            <input
                                type="email"
                                placeholder="Email Address"
                                style={{ ...inputStyle, width: '100%', marginBottom: '10px' }}
                                value={requestData.email}
                                onChange={e => setRequestData({ ...requestData, email: e.target.value })}
                            />
                            <textarea
                                placeholder={requestType === 'author'
                                    ? 'Message for author (optional)...'
                                    : 'Why do you need this thesis? (e.g., Research, Citation, etc.)...'}
                                style={{ ...inputStyle, width: '100%', height: '100px', marginBottom: '10px', resize: 'none' }}
                                value={requestData.message}
                                onChange={e => setRequestData({ ...requestData, message: e.target.value })}
                            />

                            <div style={{ display: 'flex', gap: '10px' }}>
                                {(requestType !== 'admin' || user?.role !== 'admin') && (
                                    <button
                                        onClick={requestType === 'author' ? submitDetailAuthorRequest : submitDetailRequest}
                                        disabled={isSubmitting}
                                        style={{ ...btnStyle, flex: 1, opacity: isSubmitting ? 0.7 : 1 }}
                                    >
                                        {isSubmitting ? 'Sending...' : 'Submit Request'}
                                    </button>
                                )}
                                <button
                                    onClick={() => {
                                        setShowDetailRequestForm(false);
                                        setRequestType('admin');
                                    }}
                                    style={closeBtn}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
const journalContainerStyle = {
    background: '#f9f9f9',
    borderRadius: '8px',
    border: '1px solid #ddd',
    overflow: 'hidden'
};

const homeFeatureStripStyle = {
    marginTop: '30px',
    padding: '0 16px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'stretch',
    flexWrap: 'wrap',
    gap: '0'
};

const homeFeatureColumnStyle = {
    flex: '1 1 240px',
    maxWidth: '300px',
    minWidth: '220px',
    textAlign: 'center',
    padding: '0 22px'
};

const homeFeatureKickerStyle = {
    display: 'inline-block',
    marginBottom: '10px',
    color: '#8a6a3f',
    fontSize: '0.72rem',
    fontWeight: '700',
    letterSpacing: '0.12em',
    textTransform: 'uppercase'
};

const homeFeatureTitleStyle = {
    margin: '0 0 6px 0',
    color: '#2f2417',
    fontSize: '1.3rem'
};

const homeFeatureTextStyle = {
    margin: 0,
    color: '#5c4b37',
    lineHeight: 1.6
};

const homeFeatureColumnDividerStyle = {
    width: '1px',
    alignSelf: 'stretch',
    background: 'linear-gradient(180deg, rgba(128, 0, 0, 0) 0%, rgba(128, 0, 0, 0.18) 18%, rgba(128, 0, 0, 0.18) 82%, rgba(128, 0, 0, 0) 100%)'
};

const journalHeaderStyle = {
    background: '#333',
    color: 'white',
    padding: '15px 20px',
    borderBottom: '2px solid #800000'
};

const departmentContainerStyle = {
    borderBottom: '1px solid #ddd'
};

const departmentHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: '15px 20px',
    background: '#f5f5f5',
    cursor: 'pointer',
    gap: '12px',
    borderLeft: '4px solid #800000'
};

const expandButtonStyle = {
    background: '#800000',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    width: '30px',
    height: '30px',
    fontSize: '18px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
};

const departmentNameStyle = {
    margin: 0,
    flex: 1,
    fontSize: '16px',
    fontWeight: '600',
    color: '#333'
};

const countBadgeStyle = {
    background: '#800000',
    color: 'white',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: 'bold'
};

const yearsListStyle = {
    background: '#fff',
    padding: '10px 20px 10px 50px'
};

const yearRowStyle = {
    padding: '8px 0',
    borderBottom: '1px solid #eee'
};

const thesisRowStyle = {
    width: '100%',
    padding: '8px 15px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderLeft: '3px solid #ddd',
    transition: 'all 0.2s ease',
    borderRadius: '4px',
    gap: '12px'
};

const yearNameStyle = {
    flex: 1,
    fontWeight: '500',
    color: '#333'
};

const thesisViewBtnStyle = {
    background: '#800000',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 14px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: '700',
    flexShrink: 0
};

const bannerStyle = {
    background: '#f0f7ff',
    padding: '20px',
    borderRadius: '12px',
    marginBottom: '30px',
    border: '1px solid #cce5ff',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '15px'
};

const inputStyle = {
    padding: '10px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    fontSize: '0.9rem',
    boxSizing: 'border-box'
};

const btnStyle = {
    padding: '10px 20px',
    background: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
};

const modalOverlay = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(0,0,0,0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
};

const modalContent = {
    background: 'white',
    padding: '20px',
    borderRadius: '12px',
    maxWidth: '95%',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
    overflowY: 'auto',
    boxSizing: 'border-box'
};
const requestModalBody = { maxHeight: '70vh', overflowY: 'auto', paddingRight: '6px' };
const citationStyle = { fontSize: '0.95rem', color: '#666', fontStyle: 'italic', margin: '0 0 14px 0', lineHeight: '1.5' };
const abstractBox = { background: '#f9f9f9', padding: '15px', borderRadius: '6px', fontSize: '0.95rem', lineHeight: '1.6', maxHeight: '400px', overflowY: 'auto', border: '1px solid #eee' };
const citationPanelStyle = { marginTop: '14px', padding: '14px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fafafa' };
const citationHeaderRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '10px' };
const citationFooterRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' };
const citationCountText = { color: '#666', fontSize: '0.9rem', fontWeight: 'bold' };
const copyCitationBtn = { padding: '9px 14px', background: '#800000', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' };
const readBtn = { flex: 1, padding: '12px', background: '#800000', color: 'white', borderRadius: '4px', border: 'none', cursor: 'pointer', fontWeight: 'bold' };
const downloadBtn = { flex: 1, padding: '12px', background: '#28a745', color: 'white', borderRadius: '4px', border: 'none', cursor: 'pointer', fontWeight: 'bold' };
const requestBtn = { flex: 1, padding: '12px', background: '#007bff', color: 'white', borderRadius: '4px', border: 'none', cursor: 'pointer', fontWeight: 'bold' };
const restrictedOverlay = { height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'white', padding: '40px', textAlign: 'center', background: '#333' };

const thesisPickerStyle = { background: '#f8f9fa', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px', marginBottom: '10px' };
const thesisPickerToggleStyle = { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' };

const closeBtn = {
    padding: '10px 20px',
    border: '1px solid #ddd',
    background: 'white',
    borderRadius: '4px',
    cursor: 'pointer'
};

const loaderInnerOverlay = {
    position: 'absolute',
    inset: 0,
    background: 'rgba(255,255,255,0.9)',
    zIndex: 10,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '12px'
};

const spinnerStyle = {
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #800000',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    animation: 'spin 1s linear infinite'
};

export default Home;
